/**
 * @file CompanyHealthReport.tsx
 * @brief Super Admin report — company usage league + idle flag.
 *
 * @references
 * - GET /api/admin/reports/company-health
 * - docs/product/reporting.md (sa-company-health)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Building2, UserCheck, UserX } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import {
  formatDateTime,
  formatDurationSeconds,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import { useCompanyHealthReport } from "../api";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";
import { rowCurrency } from "@/shared/utils/reportCurrency";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function CompanyHealthReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const idleDays = 30;

  const queryRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      idleDays,
    }),
    [range.startDate, range.endDate],
  );

  const report = useCompanyHealthReport(queryRange);
  const rows = report.data ?? [];
  const idleCount = rows.filter((r) => r.is_idle).length;

  return (
    <ReportShell
      title="Company health"
      description={`Usage in the selected range. Idle = no sessions in the last ${idleDays} days.`}
      range={range}
      onRangeChange={setRange}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "company-health",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-3">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Companies"
              icon={Building2}
              value={String(rows.length)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Active"
              icon={UserCheck}
              value={String(rows.length - idleCount)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Idle"
              icon={UserX}
              value={String(idleCount)}
              hint={`No sessions in ${idleDays} days`}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Companies"
          description={`${rows.length} companies · ${idleCount} idle`}
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Company",
              "Status",
              { label: "Sessions", align: "right" },
              { label: "Duration", align: "right" },
              { label: "Cost", align: "right" },
              { label: "Last session", align: "right" },
              "Idle",
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
            emptyMessage="No companies found."
          >
            {rows.map((row) => (
              <tr key={row.company_id} className="hover:bg-secondary/20">
                <td className="px-4 py-2 font-medium">
                  {row.company_name || "—"}
                </td>
                <td className="px-4 py-2 capitalize text-muted-foreground">
                  {row.company_status || "—"}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.session_count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatDurationSeconds(row.total_duration_seconds)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.total_cost_cents, rowCurrency(row))}
                </td>
                <td className="px-4 py-2 text-right text-muted-foreground">
                  {formatDateTime(row.last_session_at)}
                </td>
                <td className="px-4 py-2 text-right">
                  {row.is_idle ? (
                    <Badge variant="warning">
                      Idle
                      {row.days_since_last_session != null
                        ? ` · ${row.days_since_last_session}d`
                        : ""}
                    </Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
